import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Heart, Brain, Gift, Check } from "lucide-react";

const emotionalBenefits = [
  {
    icon: Sparkles,
    title: "Builds Confidence",
    description: "When children see themselves as heroes, they believe they can be one. It changes how they see themselves.",
    color: "bg-amber-100 text-amber-600",
  },
  {
    icon: Heart,
    title: "Creates a Bonding Moment",
    description: "Imagine reading a bedtime story to your child — where THEY are the main character. The look on their face? Priceless.",
    color: "bg-rose-100 text-rose-500",
  },
  {
    icon: Brain,
    title: "Makes Reading Fun",
    description: "Kids who don't like reading suddenly LOVE it when the story is about them.",
    color: "bg-indigo-100 text-indigo-500",
  },
  {
    icon: Gift,
    title: "The Gift They'll Never Forget",
    description: "Not another toy that breaks in 2 days. This is a keepsake they'll treasure for years.",
    color: "bg-green-100 text-green-600",
  },
];

const functionalBenefits = [
  "Personalised with your child's name and photo",
  "Premium quality printing (thick pages, vibrant colours)",
  "Indian stories — festivals, family values, desi adventures",
  "Available in English and Hindi",
  "Ships pan-India in 5-7 days",
  "Perfect for ages 3-10",
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Benefits() {
  return (
    <section id="benefits" className="py-20 sm:py-28 bg-amber-50 relative overflow-hidden" data-testid="benefits-section">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-rose-100/30 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-bold text-rose-500 uppercase tracking-widest mb-3 block">Why Parents Love Us</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-indigo-950 tracking-tight" data-testid="benefits-heading">
            Why 5,000+ Parents Love Balakatha
          </h2>
        </motion.div>

        {/* Emotional Benefits Grid */}
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
        >
          {emotionalBenefits.map((b, i) => (
            <motion.div
              key={i}
              variants={item}
              className="bg-white rounded-3xl p-6 border border-indigo-950/5 shadow-lg shadow-rose-900/5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`benefit-card-${i + 1}`}
            >
              <div className={`w-14 h-14 rounded-2xl ${b.color} flex items-center justify-center mb-5`}>
                <b.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-indigo-950 mb-2">{b.title}</h3>
              <p className="text-sm text-indigo-950/50 leading-relaxed">{b.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Functional Benefits + Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-2 gap-10 items-center"
        >
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-rose-900/10 border border-white/50">
            <img
              src="https://images.pexels.com/photos/5880155/pexels-photo-5880155.jpeg"
              alt="Happy child with personalised storybook"
              className="w-full h-[350px] object-cover"
              data-testid="benefits-image"
            />
          </div>

          <div className="bg-white rounded-3xl p-8 border border-indigo-950/5 shadow-lg" data-testid="functional-benefits-list">
            <h3 className="text-xl font-bold text-indigo-950 mb-6">What Makes Our Books Special</h3>
            <div className="space-y-4">
              {functionalBenefits.map((item, i) => (
                <div key={i} className="flex items-start gap-3 group">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-green-200 transition-colors">
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span className="text-sm sm:text-base text-indigo-950/70 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
